/**
 * Extension → media type, the ONE table.
 *
 * This is the *storage* answer (what Content-Type should this file carry),
 * NOT the display-bucket answer. Do not confuse it with `EXT_TOP_LEVEL` in
 * the files repository, which deliberately maps code extensions
 * (`.py`, `.js`, `.json`) to the `text` BUCKET for the Storage tab even
 * though their real media types are `application/*`. The two disagree on
 * purpose; merging them would silently re-bucket the Storage page.
 *
 * It lives in `@papan/types` because two very different consumers must
 * agree byte-for-byte:
 *   - the tus upload path, which stamps `mime_type` on new rows, and
 *   - `backfill:file-mime`, which repairs rows the OLD 7-entry table left
 *     as `application/octet-stream`.
 * A drifted copy would make the repair script write a different type than
 * a fresh upload of the same file, which is worse than not repairing.
 *
 * Archives are deliberately ABSENT: `application/octet-stream` is the
 * honest answer for a `.zip`/`.tar.gz`, and inventing a type for them
 * would make the backfill claim knowledge it doesn't have.
 */
export const MIME_BY_EXT: Record<string, string> = {
  csv: 'text/csv',
  json: 'application/json',
  md: 'text/markdown',
  txt: 'text/plain',
  html: 'text/html',
  parquet: 'application/octet-stream',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xls: 'application/vnd.ms-excel',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  flac: 'audio/flac',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
}

/** Media type for a filename, or `application/octet-stream` when unknown. */
export function mimeFromExt(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase()
  return MIME_BY_EXT[ext || ''] || 'application/octet-stream'
}

/**
 * Archive types. Real media types, but they say nothing about what is
 * INSIDE, so the Storage tab treats them like `octet-stream` and falls
 * back to the extension (which then lands them in "Other" on purpose).
 */
export const ARCHIVE_MIMES = [
  'application/gzip', 'application/x-gzip',
  'application/zip', 'application/x-zip-compressed',
  'application/x-tar', 'application/x-7z-compressed',
  'application/vnd.rar', 'application/x-rar-compressed',
  'application/x-bzip2', 'application/x-xz',
] as const

/**
 * Mimes that carry no usable type information — the "unknown binary"
 * placeholder the pre-2026-08 upload path stamped on everything outside
 * its 7-entry table, plus the archives above.
 *
 * ⚠ ONE definition, deliberately. This used to exist twice: as this array
 * in the files repository (interpolated into the Storage tab's SQL) and as
 * an `isUninformativeMime` here that checked ONLY `octet-stream`. The
 * function had no caller, so nothing broke — but it was exported, tested,
 * and looked authoritative, so the first caller to reach for it would have
 * bucketed archives differently from the page it was meant to match
 * (review 2026-08-06).
 */
export const UNINFORMATIVE_MIMES = ['application/octet-stream', ...ARCHIVE_MIMES] as const

/** `true` when the stored mime tells us nothing useful about the content. */
export function isUninformativeMime(mime: string | null | undefined): boolean {
  return !mime || (UNINFORMATIVE_MIMES as readonly string[]).includes(mime.toLowerCase())
}

/**
 * Source/text file extensions that should be delivered to the AGENT as text.
 *
 * A THIRD extension table, deliberately separate from `MIME_BY_EXT` (storage
 * Content-Type — code files are `octet-stream` there on purpose, so an
 * uploaded `.gs` is never served as executable `text/javascript`) and
 * `EXT_TOP_LEVEL` (the Storage-tab display bucket). This one answers only:
 * "if the browser gave us no usable type, is this attachment source/text we
 * should hand the model as text?" (#657 — browsers type Google Apps Script
 * `.gs` and most code files as `application/octet-stream`, so they never
 * reached the model). It maps to no MIME and touches no serving/storage path.
 */
export const TEXT_CODE_EXTENSIONS = new Set([
  // scripts / code
  'gs', 'js', 'mjs', 'cjs', 'jsx', 'ts', 'tsx', 'py', 'rb', 'go', 'rs',
  'java', 'kt', 'kts', 'scala', 'swift', 'c', 'h', 'cpp', 'cc', 'hpp',
  'cs', 'php', 'lua', 'pl', 'r', 'dart', 'sh', 'bash', 'zsh',
  // markup / data / config (that a browser may leave typeless)
  'sql', 'json', 'jsonc', 'xml', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf',
  'csv', 'tsv', 'md', 'markdown', 'txt', 'text', 'log', 'html', 'htm',
  'css', 'scss', 'less', 'vue', 'svelte',
])

/** `true` when a filename's extension names source/text the agent can read as
 *  text — used only to recover an attachment the browser typed as unknown. */
export function isTextCodeExt(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase()
  return !!ext && TEXT_CODE_EXTENSIONS.has(ext)
}

/**
 * Types a browser will EXECUTE when it renders them top-level: script,
 * and the two document types that can carry inline script.
 *
 * `image/svg+xml` belongs here despite looking like an image — SVG is an
 * XML document that can contain <script>.
 */
export const SCRIPTABLE_MIMES = [
  'text/html', 'application/xhtml+xml', 'image/svg+xml',
  'text/javascript', 'application/javascript', 'application/x-javascript',
  'text/xml', 'application/xml',
] as const

export function isScriptableMime(mime: string | null | undefined): boolean {
  if (!mime) return false
  const bare = mime.split(';')[0].trim().toLowerCase()
  return (SCRIPTABLE_MIMES as readonly string[]).includes(bare) || bare.endsWith('+xml')
}

/**
 * The Content-Type a RAW content route may safely echo.
 *
 * Raw routes (`/api/files/:id/content` and friends) serve bytes with no
 * sandbox — unlike `/preview/`, which resolves its own type from the
 * extension and wraps scriptable documents in a CSP sandbox. So they must
 * not let a STORED mime turn a file into an executable document, because
 * since 2026-08 that value can come from the client (`File.type` in the
 * tus `filetype` metadata) and need not agree with the filename.
 *
 * Rule: the extension decides. A stored mime is honoured only when the
 * extension has no opinion AND the stored value isn't scriptable — so
 * `report.pdf` uploaded with `filetype: text/html` serves as
 * `application/pdf`, and `x.bin` uploaded as `text/html` serves as
 * `application/octet-stream`. A genuine `.html` still serves as
 * `text/html`, because the extension agrees; rendering it safely is what
 * `/preview/` is for.
 */
export function safeRawContentType(storedMime: string | null | undefined, name: string): string {
  const byExt = mimeFromExt(name)
  if (!isUninformativeMime(byExt)) return byExt
  if (storedMime && !isScriptableMime(storedMime)) return storedMime
  return byExt
}

/**
 * May this content type be rendered INLINE by the browser, or must it be
 * forced to a download?
 *
 * Lives here, beside `safeRawContentType` and `isScriptableMime` which it
 * is built from, because two attachment routes draw the same line — the
 * chat's CAS route and the member chat-storage route — and the line is
 * what stops either of them becoming an executable-document server. It was
 * private to the first of the two, which is how the second came to need it
 * and import it ACROSS route modules, making a cycle out of a predicate.
 *
 * `isScriptableMime` first: it catches `image/svg+xml` (an XML document
 * that can carry a script) despite the `image/` prefix below.
 */
export function isSafeInlineContentType(contentType: string): boolean {
  const bare = contentType.split(';')[0].trim().toLowerCase()
  if (isScriptableMime(bare)) return false
  return bare.startsWith('image/') || bare === 'application/pdf' || bare === 'text/plain'
}
